import { ampli } from '@/ampli';
import type { SendTransactionParameters } from 'viem';

import { getBridgeProvider } from '@/lib/api/0x-cross-chain/selectCrossChainQuote';
import type {
  CrossChainEvmTransaction,
  CrossChainQuote,
  CrossChainSolanaTransaction,
} from '@/lib/api/0x-cross-chain/types';
import { ClientInputError, ClientWalletError } from '@/lib/errors/client';

import type { CrossChainTransaction } from '@/store/transaction/types';

import { trackError } from '@/utils/errors';
import type { ChainHash, EthereumAddress } from '@/utils/models';
import { amountToUsd } from '@/utils/numbers';
import { decodeRevertData, extractRevertData } from '@/utils/revertData';
import { fillSolanaOrder } from '@/utils/solana';
import { getSubmissionFailure, SUBMISSION_REASON } from '@/utils/submissionReason';
import { trackTransactionFailed } from '@/utils/transactionAnalytics';
import { getWalletClientAfterChainSwitch, isUserRejection } from '@/utils/wallet';

import { buildTokenEventFields, type FailureEvent } from '../analytics';
import type { ExecutionContext, PreflightResult, TradeStrategy } from '../types';

const GAS_BUFFER_PERCENTAGE = 12n;

type PreparedCrossChainTrade = PreflightResult & {
  quote: CrossChainQuote;
  branch: 'evm' | 'svm';
  capturedRevertReason: string | undefined;
};

function failureEvent(
  ctx: ExecutionContext,
  prepared: PreparedCrossChainTrade,
  error: unknown,
  rejected: boolean,
): FailureEvent {
  const failure = rejected ? undefined : getSubmissionFailure(error);
  return {
    sellToken: ctx.sellToken,
    buyToken: ctx.buyToken,
    failureStage: 'submission',
    failureReason: rejected ? 'user_rejected' : 'submission_error',
    submissionReason: rejected
      ? undefined
      : prepared.capturedRevertReason
        ? SUBMISSION_REASON.EXECUTION_REVERTED
        : failure?.submissionReason,
    // Prefer the EVM revert decoded at estimateGas; otherwise use the Solana detail.
    revertReason: prepared.capturedRevertReason ?? failure?.revertReason,
    swapOrderType: 'cross_chain',
    matchaReferrer: ctx.referrer.id,
    bridgeProvider: getBridgeProvider(prepared.quote.steps),
  };
}

function transactionRecord(
  ctx: ExecutionContext,
  quote: CrossChainQuote,
  hash: ChainHash,
): CrossChainTransaction {
  return {
    sellToken: ctx.sellToken,
    sellAmount: quote.sellAmount,
    sellTokenUsdPrice: ctx.tokenUsdPrices.sellTokenUsd,
    sellAmountUsd: amountToUsd(
      quote.sellAmount,
      ctx.sellToken.decimals,
      ctx.tokenUsdPrices.sellTokenUsd,
    ),
    buyToken: ctx.buyToken,
    buyAmount: quote.buyAmount,
    buyTokenUsdPrice: ctx.tokenUsdPrices.buyTokenUsd,
    buyAmountUsd: amountToUsd(
      quote.buyAmount,
      ctx.buyToken.decimals,
      ctx.tokenUsdPrices.buyTokenUsd,
    ),
    hash,
    timestamp: new Date().toISOString(),
    referrerId: undefined,
    feeSource: null,
    transactionType: 'cross-chain',
    serviceTime: quote.estimatedTimeSeconds,
    maxServiceTime: quote.estimatedTimeSeconds * 2,
    quoteId: quote.quoteId,
    quoteSteps: quote.steps,
  };
}

/** Cross-chain strategy with quote-selected EVM and SVM send branches. */
export const crossChainStrategy: TradeStrategy<PreparedCrossChainTrade> = {
  lane: 'cross-chain',
  preflight: (ctx) => {
    if (ctx.quote.source !== 'cross-chain' || !ctx.quote.raw) return null;
    const branch = ctx.quote.raw.transaction.chainType;
    if (branch === 'evm' && !ctx.ethereumWallet) return null;
    if (branch === 'svm' && !ctx.solanaWallet) return null;
    return {
      chainFamily: branch === 'evm' ? 'evm' : 'solana',
      quote: ctx.quote.raw,
      branch,
      capturedRevertReason: undefined,
    };
  },
  submit: async (ctx, prepared) => {
    ampli.swapInitiated({
      ...buildTokenEventFields({
        source: 'input',
        sellToken: ctx.sellToken,
        buyToken: ctx.buyToken,
        sellInput: ctx.sellInput,
        buyInput: ctx.buyInput,
      }),
      swap_order_type: 'cross_chain',
      matcha_referrer: ctx.referrer.id,
    });

    try {
      let hash: ChainHash;
      if (prepared.branch === 'evm') {
        if (!ctx.ethereumWallet || !ctx.publicClient) {
          const error = new ClientInputError("Can't submit a 0x cross-chain order on Ethereum");
          trackError(error);
          return { success: false, rejected: false, error };
        }
        const client = await getWalletClientAfterChainSwitch(ctx.chainId);
        if (!client) {
          const error = new ClientWalletError('Wallet client unavailable after chain switch');
          trackError(error);
          return { success: false, rejected: false, error };
        }
        const transaction = prepared.quote.transaction as CrossChainEvmTransaction;
        let gasEstimate: bigint;
        try {
          gasEstimate = await ctx.publicClient.estimateGas({
            account: ctx.ethereumWallet.address as EthereumAddress,
            data: transaction.details.data as `0x${string}`,
            to: transaction.details.to,
            value: BigInt(transaction.details.value),
          });
        } catch (error: unknown) {
          const revertData = extractRevertData(error);
          if (revertData && revertData !== '0x') {
            prepared.capturedRevertReason = decodeRevertData(revertData);
          }
          gasEstimate = BigInt(transaction.details.gas);
        }
        hash = await client.sendTransaction({
          account: ctx.ethereumWallet.address as EthereumAddress,
          chain: client.chain,
          data: transaction.details.data as `0x${string}`,
          gas: (gasEstimate * (100n + GAS_BUFFER_PERCENTAGE)) / 100n,
          to: transaction.details.to,
          value: BigInt(transaction.details.value),
        } as SendTransactionParameters);
      } else {
        if (!ctx.solanaWallet) {
          const error = new ClientInputError("Can't submit a 0x cross-chain order on Solana");
          trackError(error);
          return { success: false, rejected: false, error };
        }
        const transaction = prepared.quote.transaction as CrossChainSolanaTransaction;
        hash = await fillSolanaOrder({
          transaction: transaction.details.serializedTransaction,
          walletClient: ctx.solanaWallet,
          signTransaction: ctx.signSolanaTransaction,
        });
      }

      const tx = transactionRecord(ctx, prepared.quote, hash);
      if (prepared.branch === 'evm' && ctx.ethereumWallet) {
        ctx.deps.addTransaction(tx, ctx.ethereumWallet.address, ctx.sellToken.chainId);
        ctx.deps.addTransaction(tx, ctx.ethereumWallet.address, ctx.buyToken.chainId);
      } else if (ctx.solanaWallet) {
        const receivingWallet = ctx.ethereumWallet?.address || ctx.solanaWallet.address;
        ctx.deps.addTransaction(tx, ctx.solanaWallet.address, ctx.sellToken.chainId);
        ctx.deps.addTransaction(tx, receivingWallet, ctx.buyToken.chainId);
      }

      ampli.transactionSubmitted({
        ...buildTokenEventFields({
          source: 'quote',
          sellToken: ctx.sellToken,
          buyToken: ctx.buyToken,
          sellAmount: prepared.quote.sellAmount,
          buyAmount: prepared.quote.buyAmount,
          tokenUsdPrices: ctx.tokenUsdPrices,
        }),
        swap_order_type: 'cross_chain',
        swap_execution_type: 'standard',
        swap_tx_hash: hash,
        bridge_provider: getBridgeProvider(prepared.quote.steps),
        ...(prepared.branch === 'evm' ? { is_smart_wallet: ctx.isSmartWallet } : {}),
      });

      ctx.deps.invalidateQueries(['balance']);
      ctx.deps.invalidateQueries(['spl-token-balance']);
      ctx.deps.invalidateQueries(['readContracts']);
      ctx.deps.resetInputs();

      return { success: true, outcome: { kind: 'txHash', value: hash } };
    } catch (error: unknown) {
      const rejected = isUserRejection(error);
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      const isInsufficientGas = normalizedError.message
        .toLowerCase()
        .includes('insufficient funds for gas');
      trackTransactionFailed(failureEvent(ctx, prepared, error, rejected));
      if (!rejected && !isInsufficientGas) trackError(error);
      return { success: false, rejected, error: normalizedError };
    }
  },
};
